import { useNavigate } from 'react-router-dom'
import { buttonClasses } from './control.js'

/**
 * NavBar left-slot controls — AD-3 §4.4.
 *
 * NEVER ABSENT, NEVER CONDITIONAL ON INTERNAL PAGINATION. F5's
 * Back-appears-on-screen-2-of-a-part is precisely what a conditional slot
 * produces: the control comes and goes as she pages through one instrument, so
 * it reads as unreliable rather than as navigation.
 *
 * The slot is one of four things, chosen by where the screen sits in the app
 * and not by what is on it:
 *
 *   wordmark   home. Home is the root; there is nowhere out.
 *   Back       any screen that is not the first of a task, and every leaf.
 *   Leave      the FIRST screen of a task, where back would exit the task.
 *   nothing    a safety surface, which is exempt from the band system.
 *
 * All three are `quiet`: navigation is not an action of standing, and the one
 * forward action of the screen lives in the ActionBar.
 *
 * 44x44 minimum. AD-3 §7.8 asserts exactly one of these per route and that it
 * meets the target, which is what stops a chevron becoming a 19px glyph.
 */

export function NavBack({ to = null, label = 'Back', onBack = null }) {
  const navigate = useNavigate()
  /*
   * Three ways back, in priority order, because "back" means different things
   * at different depths and the control must do the nearest one:
   *   onBack  a step WITHIN the screen (a page of a section)
   *   to      an explicit route
   *   history whatever she came from
   */
  const press = () => {
    if (onBack) return onBack()
    if (to) return navigate(to)
    return navigate(-1)
  }
  return (
    <button
      type="button"
      onClick={press}
      className={buttonClasses('quiet') + ' -ml-[var(--s-2)]'}
    >
      {/* The chevron is decorative; the word is the control. A glyph alone is
          not a name, and screen readers should hear "Back". */}
      <span aria-hidden="true" className="mr-[var(--s-1)]">
        &lsaquo;
      </span>
      {label}
    </button>
  )
}

/**
 * The first screen of a task. Back would leave the task rather than move within
 * it, so the control says what it does.
 */
export function NavLeave({ to = '/' }) {
  return <NavBack to={to} label="Leave" />
}

/**
 * Home's left slot. Not a control — home is the root and there is nowhere out —
 * so it is a label at --t-eyebrow, which is the one place the eyebrow step is
 * used outside a task name.
 */
export function NavWordmark() {
  return <span className="t-eyebrow">Editrics</span>
}
